class DDMegaMenu extends HTMLElement {
  connectedCallback() {
    this.details = this.closest('details');
    this.summary = this.details?.querySelector('summary');
    this.summaryLink = this.summary?.dataset.ddMegaLink;
    this.categories = [...this.querySelectorAll('[data-dd-mega-category]')];
    this.panels = [...this.querySelectorAll('[data-dd-mega-panel]')];
    this.closeTarget = this.parentElement?.querySelector('[data-dd-mega-close]');
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.categories.forEach((category) => {
      const activate = () => this.activate(category.dataset.ddMegaTarget);
      category.addEventListener('pointerenter', activate, { signal });
      category.addEventListener('focus', activate, { signal });
    });

    this.summary?.addEventListener('pointerenter', this.onHoverEnter, { signal });
    this.summary?.addEventListener('pointerleave', this.onHoverLeave, { signal });
    this.summary?.addEventListener('pointerdown', this.onSummaryPointerDown, { signal });
    this.summary?.addEventListener('pointercancel', this.clearSummaryPointerToggle, { signal });
    this.summary?.addEventListener('click', this.onSummaryClick, { signal });
    this.addEventListener('pointerenter', this.onHoverEnter, { signal });
    this.addEventListener('pointerleave', this.onHoverLeave, { signal });
    this.details?.addEventListener('focusin', this.onFocusIn, { signal });
    this.details?.addEventListener('focusout', this.onFocusOut, { signal });
    this.closeTarget?.addEventListener('click', () => this.close(), { signal });
    document.querySelectorAll('details-modal.header__search > details > summary').forEach((searchSummary) => {
      searchSummary.addEventListener('click', this.onSearchOpen, { signal });
    });
    this.details?.addEventListener('keyup', this.onKeyUp, { signal });
    document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
    if (this.summary?.hasAttribute('data-dd-mega-toggle')) {
      this.summary.setAttribute('role', 'button');
      this.summary.setAttribute('aria-expanded', String(Boolean(this.details?.open)));
    } else {
      this.summary?.setAttribute('role', 'link');
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.summaryPointerTimer);
  }

  activate(targetId) {
    this.categories.forEach((category) => {
      const active = category.dataset.ddMegaTarget === targetId;
      category.classList.toggle('is-active', active);
      if (active) category.setAttribute('data-active', 'true');
      else category.removeAttribute('data-active');
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
  }

  open() {
    if (!this.details || !window.matchMedia('(min-width: 990px)').matches) return;
    document.querySelectorAll('details-modal.header__search').forEach((searchModal) => {
      if (typeof searchModal.close === 'function' && searchModal.isOpen()) searchModal.close(false);
    });
    document.querySelectorAll('.header__inline-menu details[open]').forEach((details) => {
      if (details !== this.details) {
        details.removeAttribute('open');
        details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      }
    });
    window.clearTimeout(this.closeTimer);
    this.details.open = true;
    this.summary?.setAttribute('aria-expanded', 'true');
  }

  scheduleClose() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(false), 140);
  }

  close(restoreFocus = true) {
    if (!this.details) return;
    window.clearTimeout(this.closeTimer);
    this.hoverOpened = false;
    this.details.removeAttribute('open');
    this.summary?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.summary?.focus();
  }

  onHoverEnter = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    window.clearTimeout(this.closeTimer);
    if (!this.details?.open) {
      this.hoverOpened = true;
      this.open();
    }
  };

  onHoverLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.scheduleClose();
  };

  onSummaryPointerDown = (event) => {
    if (!['touch', 'pen'].includes(event.pointerType)) return;
    this.summaryPointerToggle = { wasOpen: Boolean(this.details?.open) };
    window.clearTimeout(this.summaryPointerTimer);
    this.summaryPointerTimer = window.setTimeout(this.clearSummaryPointerToggle, 1000);
  };

  clearSummaryPointerToggle = () => {
    this.summaryPointerToggle = null;
    window.clearTimeout(this.summaryPointerTimer);
  };

  onSummaryClick = (event) => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    event.preventDefault();
    const pointerToggle = this.summaryPointerToggle;
    this.clearSummaryPointerToggle();
    if (pointerToggle) {
      this.hoverOpened = false;
      if (pointerToggle.wasOpen) this.close(false);
      else this.open();
      this.summary.setAttribute('aria-expanded', String(Boolean(this.details?.open)));
    } else if (this.summary?.hasAttribute('data-dd-mega-toggle')) {
      if (this.hoverOpened) {
        this.hoverOpened = false;
        this.open();
      }
      else if (this.details?.open) this.close(false);
      else this.open();
      this.summary.setAttribute('aria-expanded', String(Boolean(this.details?.open)));
    } else if (this.summaryLink) {
      window.location.assign(this.summaryLink);
    }
  };

  onFocusIn = () => this.open();

  onFocusOut = () => {
    window.setTimeout(() => {
      if (!this.details?.contains(document.activeElement)) this.close(false);
    }, 0);
  };

  onKeyUp = (event) => {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    event.preventDefault();
    this.close();
  };

  onSearchOpen = () => {
    if (this.details?.open) this.close(false);
  };

  onBlockSelect = (event) => {
    const selectedCard = this.querySelector(`[data-dd-about-block-id="${CSS.escape(event.detail.blockId)}"]`);
    if (selectedCard) {
      this.open();
      selectedCard.querySelector('a')?.focus();
      return;
    }
    const panel = selectedCard?.closest('[data-dd-mobile-panel]') || this.panels.find((item) => item.dataset.ddBlockId === event.detail.blockId);
    if (!panel) return;
    this.activate(panel.id);
    this.open();
  };
}

class DDMobileMenu extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('details');
    this.drawerSummary = this.drawer?.querySelector(':scope > summary');
    this.drawerSurface = this.closest('.menu-drawer');
    this.root = this.querySelector('[data-dd-mobile-root]');
    this.primaryLinks = [...this.querySelectorAll('[data-dd-mobile-section]')];
    this.homeLink = this.querySelector('[data-dd-mobile-home]');
    this.categories = [...this.querySelectorAll('[data-dd-mobile-category]')];
    this.panels = [...this.querySelectorAll('[data-dd-mobile-panel]')];
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.homeLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.showRoot({ focusTarget: this.homeLink });
    }, { signal });

    this.querySelectorAll('[data-dd-mobile-back]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showRoot();
      }, { capture: true, signal });
    });

    this.categories.forEach((category) => {
      category.setAttribute('aria-expanded', 'false');
      category.addEventListener('click', (event) => {
        event.preventDefault();
        if (category.matches('button.dd-mobile-menu__secondary-trigger')) event.stopImmediatePropagation();
        this.openPanel(category.dataset.ddMobileTarget, category);
      }, { capture: category.matches('button.dd-mobile-menu__secondary-trigger'), signal });
    });

    this.drawerSummary?.addEventListener('click', () => {
      if (!this.drawer?.open) this.reset();
    }, { signal });
    this.drawer?.addEventListener('toggle', () => {
      if (!this.drawer.open) this.reset();
    }, { signal });
    this.drawerSurface?.addEventListener('touchstart', this.onTouchStart, { passive: false, signal });
    this.drawerSurface?.addEventListener('touchmove', this.onTouchMove, { passive: false, signal });
    this.drawerSurface?.addEventListener('touchend', this.clearVerticalTouch, { passive: true, signal });
    this.drawerSurface?.addEventListener('touchcancel', this.clearVerticalTouch, { passive: true, signal });
    document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
    this.reset();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearVerticalTouch();
  }

  openPanel(targetId, trigger) {
    const activePanel = this.panels.find((panel) => panel.id === targetId);
    if (!activePanel || !this.root) return;

    this.categories.forEach((category) => {
      category.setAttribute('aria-expanded', String(category === trigger));
    });
    this.root.hidden = true;
    this.panels.forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });
    this.currentLevel = 2;
    this.dataset.ddMobileLevel = '2';
    this.activePanel = activePanel;
    this.activeCategory = trigger;
    this.setPrimarySection(activePanel.classList.contains('dd-mobile-menu__panel--about') ? 'about' : 'shop');
    this.scrollTo({ top: 0, behavior: 'auto' });
    const panelHeading = activePanel.querySelector('[data-dd-mobile-panel-heading]');
    const focusTarget = trigger?.hasAttribute('data-dd-mobile-section') ? trigger : panelHeading;
    focusTarget?.focus({ preventScroll: true });
    if (typeof refreshTrapFocus === 'function') refreshTrapFocus(this.drawer, focusTarget);
  }

  showRoot({ restoreFocus = true, focusTarget = null } = {}) {
    if (!this.root) return;
    const activeCategory = this.activeCategory;
    this.reset();
    this.scrollTo({ top: 0, behavior: 'auto' });
    const nextFocus = focusTarget || activeCategory || this.root.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (restoreFocus) nextFocus?.focus();
    if (typeof refreshTrapFocus === 'function') refreshTrapFocus(this.drawer, nextFocus);
  }

  setPrimarySection(section) {
    this.primaryLinks.forEach((link) => {
      const active = link.dataset.ddMobileSection === section;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  reset() {
    if (!this.root) return;
    this.root.hidden = false;
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
    this.categories.forEach((category) => category.setAttribute('aria-expanded', 'false'));
    this.currentLevel = 1;
    this.dataset.ddMobileLevel = '1';
    this.activePanel = null;
    this.activeCategory = null;
    this.setPrimarySection('shop');
    this.scrollTo({ top: 0, behavior: 'auto' });
    this.clearVerticalTouch();
  }

  onTouchStart = (event) => {
    this.clearVerticalTouch();
    if (!this.drawer?.open || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.verticalTouch = {
      identifier: touch.identifier,
      startX: touch.clientX,
      startY: touch.clientY,
    };
  };

  onTouchMove = (event) => {
    const verticalTouch = this.verticalTouch
      ? Array.from(event.touches).find((item) => item.identifier === this.verticalTouch.identifier)
      : null;
    if (verticalTouch) {
      const verticalDeltaX = verticalTouch.clientX - this.verticalTouch.startX;
      const verticalDeltaY = verticalTouch.clientY - this.verticalTouch.startY;
      const isVerticalGesture = Math.abs(verticalDeltaY) > Math.abs(verticalDeltaX);
      const atTop = this.scrollTop <= 0;
      const atBottom = this.scrollTop + this.clientHeight >= this.scrollHeight - 1;
      if (isVerticalGesture && ((atTop && verticalDeltaY > 0) || (atBottom && verticalDeltaY < 0))) {
        event.preventDefault();
      }
    }
  };

  clearVerticalTouch = () => {
    this.verticalTouch = null;
  };

  onBlockSelect = (event) => {
    const selectedCard = this.querySelector(`[data-dd-about-block-id="${CSS.escape(event.detail.blockId)}"]`);
    const panel = selectedCard?.closest('[data-dd-mobile-panel]') || this.panels.find((item) => item.dataset.ddBlockId === event.detail.blockId);
    const category = this.categories.find((item) => item.dataset.ddMobileTarget === panel?.id);
    if (!panel || !category) return;
    if (this.drawer) {
      this.drawer.open = true;
      this.drawer.classList.add('menu-opening');
      this.drawer.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    }
    this.openPanel(panel.id, category);
  };
}

if (!customElements.get('dd-mega-menu')) customElements.define('dd-mega-menu', DDMegaMenu);
if (!customElements.get('dd-mobile-menu')) customElements.define('dd-mobile-menu', DDMobileMenu);
